import { useMemo, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ListTodo, GripVertical, Plus, Trash2, Clock, MapPin } from "lucide-react";
import { confirm } from "@/components/ConfirmDialog";

const hm = (m) => `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`;

export function DropModeToggle({ mode, setMode, testid }) {
  const opt = (v, label, title) => (
    <button type="button" title={title} onClick={() => setMode(v)} data-testid={`${testid}-${v}`}
      className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors ${mode === v ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{label}</button>
  );
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-slate-200 p-0.5 bg-white" data-testid={testid}>
      <span className="text-[11px] text-slate-500 px-1">On drop:</span>
      {opt("move", "Move", "Book it and take it off the list")}
      {opt("copy", "Copy", "Book a visit and keep it on the list for the next visit (same job number)")}
    </div>
  );
}

function AddQueueDialog({ open, onOpenChange, clients, properties, onAdded }) {
  const [f, setF] = useState({ clientId: "", propertyId: "", hours: "2", notes: "" });
  const [busy, setBusy] = useState(false);
  const props = useMemo(() => properties.filter((p) => !f.clientId || p.clientId === f.clientId), [properties, f.clientId]);
  const save = async () => {
    setBusy(true);
    try {
      await client.post("/job-queue", { clientId: f.clientId, propertyId: f.propertyId, durationMinutes: Math.round(Number(f.hours) * 60), instructions: f.notes });
      toast.success("Added to queue"); setF({ clientId: "", propertyId: "", hours: "2", notes: "" }); onAdded(); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="queue-add-dialog">
        <DialogHeader><DialogTitle>Add a queued job</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={f.clientId} onValueChange={(v) => setF({ ...f, clientId: v, propertyId: "" })}>
            <SelectTrigger data-testid="queue-add-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.propertyId} onValueChange={(v) => setF({ ...f, propertyId: v, clientId: f.clientId || (properties.find((p) => p.id === v) || {}).clientId || "" })}>
            <SelectTrigger data-testid="queue-add-property"><SelectValue placeholder="Property" /></SelectTrigger>
            <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600 w-32">Length (hours)</label>
            <Input type="number" min="0.25" step="0.25" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} className="w-28" data-testid="queue-add-hours" />
          </div>
          <Textarea placeholder="Instructions (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="queue-add-notes" />
        </div>
        <DialogFooter>
          <Button disabled={busy || !f.clientId || !f.propertyId || !(Number(f.hours) >= 0.25)} onClick={save} className="bg-brand hover:bg-brand-dark" data-testid="queue-add-save">Add to queue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QueueItem({ it, canManage, onDragStart, onDragEnd, onRemove }) {
  return (
    <div data-testid={`queue-item-${it.id}`} draggable={canManage} onDragStart={() => onDragStart(it)} onDragEnd={onDragEnd}
      className={`rounded-lg border border-violet-200 bg-violet-50/50 p-2.5 text-xs ${canManage ? "cursor-grab active:cursor-grabbing hover:border-violet-400 hover:shadow-sm" : ""}`}>
      <div className="flex items-start gap-2">
        {canManage && <GripVertical className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />}
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-800 truncate">{it.jobNumber ? `#${it.jobNumber} · ` : ""}{it.address}</p>
          <p className="text-slate-600 truncate">{it.clientName}</p>
          <p className="text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{hm(it.durationMinutes || 120)}
            {it.geo?.lat == null && <span className="ml-1 text-amber-600 flex items-center gap-0.5"><MapPin className="h-3 w-3" />no map pin</span>}</p>
        </div>
        {canManage && <button onClick={() => onRemove(it)} className="text-slate-300 hover:text-red-600" data-testid={`queue-remove-${it.id}`} title="Delete from queue"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

export default function QueuePanel({ items, canManage, clients, properties, onChanged, onDragStart, onDragEnd, draggingEvent, onDropEvent, showOnMap, setShowOnMap, dropMode, setDropMode }) {
  const [addOpen, setAddOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const remove = async (it) => {
    if (!(await confirm({ title: "Delete queued job", description: `Remove ${it.address} from the queue for good?`, confirmText: "Delete", destructive: true }))) return;
    try { await client.delete(`/job-queue/${it.id}`); toast.success("Removed"); onChanged(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const dropping = !!draggingEvent;
  return (
    <Card className={`p-4 transition-colors ${dropping ? (hover ? "border-violet-500 bg-violet-50 ring-2 ring-violet-300" : "border-violet-300 border-dashed") : "border-slate-200"}`} data-testid="queue-panel"
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onMouseUp={() => { if (draggingEvent) onDropEvent(draggingEvent); }}>
      <div className="flex items-center gap-2 mb-1">
        <ListTodo className="h-4 w-4 text-violet-600" />
        <h3 className="font-semibold text-slate-900 text-sm flex-1">Queued jobs <span className="text-violet-700" data-testid="queue-count">({items.length})</span></h3>
        {canManage && <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-violet-300 text-violet-700" onClick={() => setAddOpen(true)} data-testid="queue-add-btn"><Plus className="h-3.5 w-3.5 mr-0.5" />Add</Button>}
      </div>
      <p className="text-[11px] text-slate-400 mb-2">{dropping ? "Release here to move this job back to the queue." : "Drag onto the calendar to book. Drag a one-off job here to queue it."}</p>
      {canManage && <div className="mb-2"><DropModeToggle mode={dropMode} setMode={setDropMode} testid="queue-drop-mode" /></div>}
      <label className="flex items-center gap-1.5 text-xs text-slate-600 mb-2 cursor-pointer">
        <input type="checkbox" checked={showOnMap} onChange={(e) => setShowOnMap(e.target.checked)} data-testid="queue-show-map" />Show queued jobs on the map
      </label>
      {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="queue-empty">Nothing waiting.</p> : (
        <div className="space-y-2 max-h-[380px] overflow-y-auto">
          {items.map((it) => <QueueItem key={it.id} it={it} canManage={canManage} onDragStart={onDragStart} onDragEnd={onDragEnd} onRemove={remove} />)}
        </div>
      )}
      <AddQueueDialog open={addOpen} onOpenChange={setAddOpen} clients={clients} properties={properties} onAdded={onChanged} />
    </Card>
  );
}
